import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { softenWhitelist } from './lib/whitelist-wording';
import en from './locales/en';
import neutral from './locales/neutral';
import ru from './locales/ru';

const LOCALE_STORAGE_KEY = 'client_locale';

/** Стартовый язык: выбор из прошлой сессии, иначе язык Telegram, иначе русский. */
function initialLocale(): string {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved === 'ru' || saved === 'en') return saved;
  } catch {
    // ignore
  }
  const tg = (window as { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { language_code?: string } } } } })
    .Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
  return tg?.split('-')[0] === 'en' ? 'en' : 'ru';
}

const resources = {
  en: { translation: en },
  ru: { translation: ru },
};

// Тумблер бота «без белых списков, обходов и анонимности» (`lib/whitelist-wording.ts`):
// меняет наши тексты, а ключи, переписанные партнёром, показываем как он написал.
let hideWhitelist = false;
const partnerKeys = new Set<string>();

// Исходные тексты ключей с нейтральными правками. Снимок до init: i18next хранит
// словари по ссылке, и `addResource` переписал бы исходник, вернуть было бы нечего.
const neutralOriginals = {
  ru: Object.fromEntries(Object.keys(neutral.ru).map((k) => [k, ru[k]])),
  en: Object.fromEntries(Object.keys(neutral.en).map((k) => [k, en[k]])),
} as Record<'ru' | 'en', Record<string, string | undefined>>;

i18n
  .use(initReactI18next)
  .use({
    type: 'postProcessor',
    name: 'whitelistWording',
    process(value: string, key: string | string[]) {
      const k = Array.isArray(key) ? key[0] : key;
      return hideWhitelist && !partnerKeys.has(k) ? softenWhitelist(value) : value;
    },
  })
  // Числа в текстах с разрядами: «12 847 оценок», «Не хватает 1 491 ₽». Без этого
  // i18next подставлял их как есть, и пятизначная сумма читалась с запинкой. Форматов
  // вида `{{x, number}}` в словарях нет, поэтому своего форматтера хватает.
  .use({
    type: 'formatter',
    init() {},
    add() {},
    addCached() {},
    format(value: unknown, format: string | undefined, lng: string | undefined) {
      if (format || typeof value !== 'number' || !Number.isFinite(value)) return value;
      return value.toLocaleString(lng === 'en' ? 'en-GB' : 'ru-RU');
    },
  })
  .init({
    resources,
    lng: initialLocale(),
    fallbackLng: "en",
    interpolation: { escapeValue: false, alwaysFormat: true },
    postProcess: ['whitelistWording'],
  });

/** Включить нейтральные тексты по тумблеру бота (из auth-ответа). */
export function setHideWhitelist(on: boolean): void {
  if (hideWhitelist === on) return;
  hideWhitelist = on;
  applyNeutral(on);
  i18n.changeLanguage(i18n.language);
}

/** Наложить (или снять) точечные правки `locales/neutral.ts`. */
function applyNeutral(on: boolean): void {
  for (const lng of ['ru', 'en'] as const) {
    for (const [key, pairs] of Object.entries(neutral[lng])) {
      const original = neutralOriginals[lng][key];
      if (original === undefined || partnerKeys.has(key)) continue;
      const value = on ? pairs.reduce((text, [from, to]) => text.split(from).join(to), original) : original;
      i18n.addResource(lng, 'translation', key, value);
    }
  }
}

/**
 * Наложить партнёрские override текстов мини-аппа поверх дефолтов.
 *
 * Приходят из auth-ответа (`/api/client/auth/telegram-webapp` → `webapp_texts`).
 * Override одноязычный: один и тот же текст пишем и в `ru`, и в `en`, чтобы клиент
 * видел партнёрский текст независимо от своей локали. Нетронутые ключи остаются
 * двуязычными (fallback из `resources`). Бэк отдаёт только whitelisted-ключи,
 * так что чужого/системного текста тут быть не может.
 */
export function applyTextOverrides(map?: Record<string, string>): void {
  if (!map) return;
  let touched = false;
  for (const [key, value] of Object.entries(map)) {
    if (typeof value !== 'string' || !value) continue;
    i18n.addResource('ru', 'translation', key, value);
    i18n.addResource('en', 'translation', key, value);
    partnerKeys.add(key);
    touched = true;
  }
  // Форс-ре-рендер уже смонтированных компонентов (addResource сам событие не шлёт).
  if (touched) i18n.changeLanguage(i18n.language);
}

/**
 * Переключить язык интерфейса.
 *
 * Отдельная функция, а не голый `changeLanguage`: язык надо ещё и запомнить, иначе
 * при следующем открытии мини-аппа он сбросится на дефолт, пока не приедет
 * auth-ответ, — и человек увидит мигание.
 */
export function applyLocale(locale: string): void {
  const lng = locale === 'en' ? 'en' : 'ru';
  if (i18n.language !== lng) void i18n.changeLanguage(lng);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, lng);
  } catch {
    // приватный режим — переживём, язык приедет из auth-ответа
  }
}

/** Язык из прошлой сессии — чтобы первый кадр не мигал русским у англоязычного. */
export function storedLocale(): string | null {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export default i18n;
